import {
  MultipleSearchParams,
  PlainJSONSearchParams,
  RangeSearchParams,
  SearchModality,
  SearchParams,
  SearchType,
  SearchValue,
  SingleSearchParams,
} from '@/components/search/types';

// get the correct format for a search modality - '1234 - 1235' or '1234'
export const formatSearchModality = (searchParams?: SearchParams | null) => {
  if (searchParams) {
    if (searchParams.searchModality === 'range') return `${searchParams?.fromValue} - ${searchParams?.toValue}`;
    if (!searchParams?.searchValue) return '';
    return searchParams?.searchValue?.toString();
  }
};

export const formatForValidOmloop = (query: string) => {
  return query
    .trim()
    .split(/[\s,;]+/)
    .filter((s) => s.trim().length > 0)
    .map((s) => {
      const match = s.match(/^([a-zA-Z]+)(\d*)$/);
      if (match) {
        return match[2] ? `${match[1].toUpperCase()}-${match[2]}` : `${match[1].toUpperCase()}*`;
      }
      return s.toUpperCase();
    });
};

export const parseValidRange = (query: string) => {
  const matches = query.match(/^(\d+)-(\d+)$/);
  if (matches && matches.length > 2) {
    const [__, fromValue, toValue] = matches.map(Number);
    if (toValue > fromValue) {
      return [fromValue, toValue];
    }
    return null;
  }
};

export const parseValidEnumeration = (value: string) => {
  const validEnumerationRegex = /^(\d{1,14}[\s,;]+)+\d{1,14}[\s,;]*$/; // NOSONAR
  if (validEnumerationRegex.test(value)) {
    return value
      .split(/[\s,;]+/)
      .filter((s) => s.trim().length > 0)
      .map(Number);
  }
  return null;
};

export const validateJson = (query: string) => {
  try {
    JSON.parse(query);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

/** Validate the searchQuery input. */
export function validateSearchQuery({
  query,
  searchType,
  isWithinAdvancedSearch,
}: {
  query: string;
  searchType: SearchType;
  isWithinAdvancedSearch: boolean;
}) {
  const input = query.trim();
  const hasLength = query.length > 0;

  // Identify the searchQuery and validate its contents.
  // Only tests numeric values for searchTypes: 'train, rollingStock, trainAndRollingStock, emtpy'
  const isValidSingle = /^\d{1,14}$/.test(input); // Only tests numeric values for searchTypes: 'train, rollingStock, trainAndRollingStock, emtpy'
  const isValidMultiple = /^(\d{1,14}[\s,;]+)+\d{1,14}[\s,;]*$/.test(input); // Only tests numeric values for searchTypes: 'train, rollingStock, trainAndRollingStock, emtpy'
  const isValidRange =
    (['train', 'rollingStock', 'empty'].includes(searchType) ||
      (searchType === 'trainAndRollingStock' && !isWithinAdvancedSearch)) &&
    parseValidRange(input);

  const isValidJson = searchType === 'json' && hasLength && validateJson(input);
  const isValidMaterieelTypeValue = searchType === 'rollingStockType' && hasLength && /^[\w,\-\s+]+$/.test(input);
  const isValidOmloopValue =
    searchType === 'omloop' && hasLength && /^([a-zA-Z]{2,3}\*?-?\d*[\s,;:]*)*[a-zA-Z]{2,3}\*?-?\d*$/.test(input);
  const isValidBijzonderheidscode =
    searchType === 'bijzonderheidscode' &&
    hasLength &&
    !input.startsWith('{') &&
    !input.startsWith('[') &&
    /([A-Za-z]+,? ?)/gm.test(input);

  const isValidSingleOrMultipleValue = isValidSingle || isValidMultiple;
  const isValidSingleMultipleOrRangeValue = Boolean(isValidSingle || isValidMultiple || Array.isArray(isValidRange));

  /** Interpret the validations and check for errors. */
  const hasValidationError = () => {
    if (!hasLength) return false; // Defaults to no error.
    if (input.startsWith('{') || input.startsWith('[')) return !isValidJson;
    if (searchType === 'trainAndRollingStock')
      return isWithinAdvancedSearch ? !isValidSingleOrMultipleValue : !isValidSingleMultipleOrRangeValue;

    return !isValidSingle && !isValidMultiple && !isValidRange;
  };
  const hasError =
    hasValidationError() &&
    hasLength &&
    !isValidMaterieelTypeValue &&
    !isValidOmloopValue &&
    !isValidBijzonderheidscode;

  /** Determine the searchModality */
  const getSearchModality = (): SearchModality => {
    if (isValidJson) return 'json';
    if (isValidRange) return 'range';
    // isValidMaterieelTypeValue and isValidOmloopValue are added for backwards compatibility
    if (isValidMultiple || isValidMaterieelTypeValue || isValidOmloopValue) return 'multiple';
    if (isValidBijzonderheidscode && /[,;]/.test(input)) return 'multiple';
    return 'single';
  };
  const searchModality = getSearchModality();

  /** Get the 'searchValue' (single | multiple | json) or 'fromValue' and 'toValue' (range) */
  const getSearchValues = () => {
    const values = {
      searchValue: null,
      fromValue: null,
      toValue: null,
    } as {
      searchValue: SearchValue | null;
      fromValue: number | null;
      toValue: number | null;
    };
    if (Array.isArray(isValidRange)) {
      values.fromValue = isValidRange?.[0] ?? null;
      values.toValue = isValidRange?.[1] ?? null;
    } else {
      if (isValidSingle) values.searchValue = Number(input);
      if (isValidMultiple) values.searchValue = parseValidEnumeration(input) ?? [];
      if (isValidJson) values.searchValue = input;
      if (isValidMaterieelTypeValue)
        values.searchValue = input
          .trim()
          .replace(/,\s*$/, '')
          .split(/[,;]+/)
          .filter((s) => s.trim().length > 0);
      if (isValidOmloopValue) values.searchValue = formatForValidOmloop(query);
      if (isValidBijzonderheidscode) {
        const parts = input
          .trim()
          .split(/[,;]+/)
          .map((s) => s.trim().toUpperCase())
          .filter((s) => s.length > 0);
        values.searchValue = parts.length > 1 ? parts : parts[0];
      }
    }
    return values;
  };
  const { searchValue, fromValue, toValue } = getSearchValues();

  const createSearchParams = ({ searchDate = null }: { searchDate?: string | null }): SearchParams | undefined => {
    if (hasError) return;
    const resolvedSearchType: SearchType = (() => {
      if (searchType === 'empty') return 'trainAndRollingStock';
      return searchType;
    })();
    const searchParams: Partial<SearchParams> = {
      searchModality,
      searchType: resolvedSearchType,
    };
    if (searchModality !== 'json') {
      searchParams.searchDate = searchDate ?? null;
      if (searchDate) {
        const descriptionDate = searchDate.split('-');
        descriptionDate.reverse();
        searchParams.description = ` op ${descriptionDate.join('-')}`;
      } else searchParams.description = ' ';
    }
    if (searchModality === 'range' && Array.isArray(isValidRange)) {
      (searchParams as RangeSearchParams).fromValue = isValidRange?.[0] ?? null;
      (searchParams as RangeSearchParams).toValue = isValidRange?.[1] ?? null;
    } else if (searchValue !== null) {
      (searchParams as SingleSearchParams | MultipleSearchParams | PlainJSONSearchParams).searchValue = searchValue;
    }
    return searchParams as SearchParams;
  };

  return {
    createSearchParams,
    fromValue,
    hasError,
    isValid: !hasError,
    isValidJson,
    isValidMaterieelTypeValue,
    isValidMultiple,
    isValidOmloopValue,
    isValidRange,
    isValidSingle,
    isValidSingleMultipleOrRangeValue,
    isValidSingleOrMultipleValue,
    isValidBijzonderheidscode,
    searchModality,
    searchValue,
    toValue,
  };
}
